import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { createPortal } from 'react-dom';
import { Input, InputStates } from '../input';
import styles from './date-picker.module.scss';
import arrowIcon from './icons/arrow.svg';
import calendarIcon from './icons/calendar.svg';
import arrowDifIcon from './icons/arrow-dif.svg';
//TODO: если этот компонент будет жить, то его надо будет перенести в /features

const DAYS_IN_WEEK = 7;
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

export type DatePickerProps = {
  className?: string;
  placeholder?: string;
  selectedDate?: Date | null;
  selectDate: (newDate: Date | null) => void;
  isError?: boolean;
};

/**
 * Date picker
 * @param className
 * @param placeholder
 * @param selectedDate
 * @param selectDate
 * @param isError
 */
export const DatePicker = ({
  className,
  placeholder = 'Выберите дату',
  selectDate,
  selectedDate,
  isError,
}: DatePickerProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [isMonthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const inputRef = useRef<HTMLDivElement>(null);
  const dataPickerRef = useRef<HTMLDivElement>(null);

  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const startDayIndex = startOfMonth.getDay() || 7;
  const daysInMonth = endOfMonth.getDate();

  const prevMonthDays = useMemo(
    () =>
      Array.from({ length: startDayIndex - 1 }, (_, i) =>
        new Date(currentDate.getFullYear(), currentDate.getMonth(), -i).getDate(),
      ).reverse(),
    [currentDate, startDayIndex],
  );

  const nextMonthDays = useMemo(
    () => Array.from({ length: DAYS_IN_WEEK * 6 - daysInMonth - startDayIndex + 1 }, (_, i) => i + 1),
    [daysInMonth, startDayIndex],
  );

  const daysGrid: {
    day: number;
    outside: boolean;
    isPast?: boolean;
  }[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return [
      ...prevMonthDays.map((day) => ({ day, outside: true })),
      ...Array.from({ length: daysInMonth }, (_, i) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1);
        return {
          day: i + 1,
          outside: false,
          isPast: date < today,
        };
      }),
      ...nextMonthDays.map((day) => ({ day, outside: true })),
    ];
  }, [currentDate, daysInMonth, nextMonthDays, prevMonthDays]);

  const changeMonth = (direction: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const toggleDatePicker = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
    setIsOpen(!isOpen);
  };

  const closeDatePicker = () => {
    setIsOpen(false);
  };

  const handleDayClick = (day: number, isOutside: boolean) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + (isOutside ? (day > 15 ? -1 : 1) : 0),
      day,
    );
    selectDate(newDate);
  };

  const handleMonthSelect = (monthIndex: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), monthIndex, 1));
    setMonthDropdownOpen(false);
  };

  const handleYearSelect = (year: number) => {
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setYearDropdownOpen(false);
  };

  const DatePicker = () => (
    <div ref={dataPickerRef} className={styles.datePicker} style={{ top: position.top, left: position.left }}>
      <div className={styles.header}>
        <span className={styles.arrow} onClick={() => changeMonth(-1)}>
          <img src={arrowDifIcon} alt="Стрелка влево" />
        </span>

        <div className={styles.dropdownWrapper}>
          <span onClick={() => setMonthDropdownOpen(!isMonthDropdownOpen)}>
            {MONTHS[currentDate.getMonth()]}
            <img src={arrowIcon} alt="Стрелка вниз" />
          </span>
          {isMonthDropdownOpen && (
            <div className={styles.dropdown}>
              {MONTHS.map((month, index) => (
                <div key={month} onClick={() => handleMonthSelect(index)}>
                  {month}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.dropdownWrapper}>
          <span onClick={() => setYearDropdownOpen(!isYearDropdownOpen)}>
            {currentDate.getFullYear()}
            <img src={arrowIcon} alt="Стрелка вниз" />
          </span>
          {isYearDropdownOpen && (
            <div className={styles.dropdown}>
              {Array.from({ length: 10 }, (_, i) => (
                <div key={i} onClick={() => handleYearSelect(currentDate.getFullYear() + i - 1)}>
                  {currentDate.getFullYear() + i - 1}
                </div>
              ))}
            </div>
          )}
        </div>

        <span className={styles.arrow} onClick={() => changeMonth(1)}>
          <img src={arrowDifIcon} alt="Стрелка влево" />
        </span>
      </div>
      <div className={styles.weekdays}>
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className={cn(styles.weekday, {
              [styles.holiday]: day === 'Сб' || day === 'Вс',
            })}
          >
            {day}
          </div>
        ))}
      </div>
      <div className={styles.calendar}>
        {daysGrid.map(({ day, outside, isPast }, index) => (
          <div
            key={index}
            className={cn(styles.day, {
              [styles.outside]: outside,
              [styles.disabled]: isPast,
              [styles.selected]:
                !outside &&
                selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === currentDate.getMonth() &&
                selectedDate.getFullYear() === currentDate.getFullYear(),
            })}
            onClick={() => !outside && !isPast && handleDayClick(day, outside)}
          >
            {day}
          </div>
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        dataPickerRef.current &&
        !dataPickerRef.current.contains(event.target as Node)
      ) {
        closeDatePicker();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={cn(styles.datePickerWrapper, className)} ref={inputRef}>
      <Input
        state={selectedDate ? InputStates.choosen : undefined}
        value={selectedDate ? selectedDate.toLocaleDateString('ru-RU') : ''}
        placeholder={placeholder}
        onClick={toggleDatePicker}
        icon={calendarIcon}
        isError={isError}
      />

      {isOpen && createPortal(<DatePicker />, document.body)}
    </div>
  );
};

export default DatePicker;
